import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Search, X } from "lucide-react";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

export default function MaterialPendingReport() {
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("all");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/reports/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const load = () => {
    setBusy(true);
    const params = {};
    if (customerFilter !== "all") params.customer_name = customerFilter;
    api.get("/reports/materials-pending-by-customer", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerFilter]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data.rows;
    const needle = q.toLowerCase();
    return data.rows.filter((r) =>
      (r.customer_name || "").toLowerCase().includes(needle) ||
      (r.material_code || "").toLowerCase().includes(needle) ||
      (r.material_name || "").toLowerCase().includes(needle)
    );
  }, [data, q]);

  const groups = useMemo(() => {
    const map = {};
    for (const r of filteredRows) {
      if (!map[r.customer_name]) map[r.customer_name] = [];
      map[r.customer_name].push(r);
    }
    return Object.entries(map)
      .map(([name, rows]) => ({
        customer_name: name, rows,
        total_jobs: rows.reduce((s, r) => s + r.jobs_allocated, 0),
        total_planned: rows.reduce((s, r) => s + r.meters_planned, 0),
        materials_count: rows.length,
      }))
      .sort((a, b) => b.total_planned - a.total_planned);
  }, [filteredRows]);

  const grandTotals = useMemo(() => filteredRows.reduce(
    (s, r) => ({ jobs: s.jobs + r.jobs_allocated, planned: s.planned + r.meters_planned }),
    { jobs: 0, planned: 0 }
  ), [filteredRows]);

  const exportCSV = () => downloadCSV(
    `material-pending_${customerFilter !== "all" ? customerFilter.replace(/\s+/g, "-") : "all"}`,
    ["Customer Name", "Material Code", "Material Name", "No. of Jobs Allocated", "Material Width", "Meters Planned", "Meters Available"],
    filteredRows.map((r) => [
      r.customer_name, r.material_code, r.material_name, r.jobs_allocated,
      r.material_width || "", r.meters_planned, r.meters_available,
    ])
  );

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">Customer filter</label>
          <div className="flex items-center gap-1">
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger data-testid="report-customer-filter" className="rounded-sm w-full max-w-80">
                <SelectValue placeholder="All customers" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All customers</SelectItem>
                {customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {customerFilter !== "all" && (
              <button data-testid="report-clear-customer" onClick={() => setCustomerFilter("all")}
                className="p-1.5 hover:bg-neutral-100 rounded-sm" title="Clear filter">
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 min-w-64">
          <label className="label-caps block mb-1">Search material / customer</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="report-search" value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-8 rounded-sm" placeholder="e.g. AMA2030 or Eugia" />
          </div>
        </div>
        <Button data-testid="report-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Refresh"}
        </Button>
        <Button data-testid="report-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <Kpi label="Customers with pending orders" value={groups.length} />
        <Kpi label="Total jobs allocated" value={inr(grandTotals.jobs)} />
        <Kpi label="Total meters planned" value={inr(grandTotals.planned)} suffix="m" />
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="material-pending-table">
          <thead className="bg-neutral-50 label-caps">
            <tr>
              <th className="text-left px-4 py-2.5 w-64">Customer Name</th>
              <th className="text-left px-4 py-2.5">Material Code</th>
              <th className="text-right px-4 py-2.5">No. of Jobs Allocated</th>
              <th className="text-right px-4 py-2.5">Material Width</th>
              <th className="text-right px-4 py-2.5">Meters Planned</th>
              <th className="text-right px-4 py-2.5">Meters Available</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && <EmptyRow cols={6} msg="No pending material-linked jobs for this filter." />}
            {groups.map((g) => <CustomerGroupRows key={g.customer_name} group={g} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CustomerGroupRows({ group }) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <tr className="border-t-2 border-neutral-200 bg-neutral-50/60 cursor-pointer" onClick={() => setOpen(!open)}>
        <td className="px-4 py-2.5" rowSpan={open ? group.rows.length + 1 : 1}>
          <button data-testid={`report-group-toggle-${group.customer_name}`}
            className="flex items-start gap-1.5 font-medium text-left">
            {open ? <ChevronDown size={14} className="mt-0.5 text-neutral-500 shrink-0" /> : <ChevronRight size={14} className="mt-0.5 text-neutral-500 shrink-0" />}
            <div>
              <div className="text-neutral-900">{group.customer_name}</div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5">
                {group.materials_count} material{group.materials_count !== 1 && "s"} · {group.total_jobs} jobs · {inr(group.total_planned)} m
              </div>
            </div>
          </button>
        </td>
      </tr>
      {open && group.rows.map((r, idx) => {
        const short = r.meters_available < r.meters_planned;
        return (
          <tr key={`${r.customer_name}-${r.material_code}-${idx}`} className="border-t border-neutral-100 data-row"
            data-testid={`report-row-${r.material_code}`}>
            <td className="px-4 py-2.5 font-mono-num text-xs">
              {r.material_code}
              {r.material_name && <div className="text-neutral-400 text-[11px] font-sans">{r.material_name}</div>}
            </td>
            <td className="px-4 py-2.5 text-right font-mono-num">{r.jobs_allocated}</td>
            <td className="px-4 py-2.5 text-right font-mono-num text-neutral-600">{r.material_width || "—"}</td>
            <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(r.meters_planned)}</td>
            <td className={`px-4 py-2.5 text-right font-mono-num ${
              r.meters_available === 0 ? "text-rose-700 font-semibold" :
              short ? "text-amber-700" : "text-emerald-700"
            }`}>
              {inr(r.meters_available)}
              {short && r.meters_available > 0 && (
                <div className="text-[10px] uppercase tracking-widest text-amber-600">short by {inr(r.meters_planned - r.meters_available)}</div>
              )}
              {r.meters_available === 0 && (
                <div className="text-[10px] uppercase tracking-widest text-rose-600">out of stock</div>
              )}
            </td>
          </tr>
        );
      })}
    </>
  );
}

// ============================================================================
// 2. Inventory Value & Age
// ============================================================================
